import { ApolloClient, gql, NormalizedCacheObject } from '@copybet/apollo';
import { DTOBetOutcomeEnum, DTOBetTypeEnum, Period } from '@copybet/core';
import { DTOQuery } from '@copybet/dto';

export const getCurrentUserExtendedData = async (client: ApolloClient<NormalizedCacheObject>) => {
  try {
    const dataRequest = await client.query<Pick<DTOQuery, 'getCurrentUser'>>({
      query: gql`
        query GetUserRestrictions {
          getCurrentUser {
            id
            email
            avatar
            avatarColor
            avatarSymbol
            nickname
            newsSubscription
            isTest
            isEmailConfirmed
            isFullyVerified
            timeZone
            registrationMethod
            letterLocale
            isActive
            isLocked
            loggedInAt {
              id
              dateTime
              formattedDateTime
              formattedDate
            }
            isBlockedToReceivingBonuses
            blockedBonuses
            signalPushEnabled
            sportsbookPushEnabled
            followingCount
            followersCount
            userFlags {
              showSignalBadgeNew
            }
          }
        }
      `,
    });

    return dataRequest.data.getCurrentUser;
  } catch (error) {
    console.error('Error fetching extended user data at getCurrentUserExtendedData', error);
    return null;
  }
};

export const getVerificationReviewStatus = async (client: ApolloClient<NormalizedCacheObject>) => {
  try {
    const dataRequest = await client.query<Pick<DTOQuery, 'getVerificationReviewStatus'>>({
      query: gql`
        query GetVerificationReviewStatus {
          getVerificationReviewStatus {
            choosenLevelName
            reviewStatusEnum
          }
        }
      `,
    });

    return dataRequest.data.getVerificationReviewStatus;
  } catch (error) {
    console.error('Error fetching extended user data at getVerificationReviewStatus', error);
    return null;
  }
};

export const getCurrentUserBriefData = async (client: ApolloClient<NormalizedCacheObject>) => {
  try {
    const userBriefData = await client.query<Pick<DTOQuery, 'getUserBriefInformation'>>({
      query: gql`
        query LoadUserBriefData {
          getUserBriefInformation {
            isBlockedToReceivingBonuses
            isFullyVerified
            isLocked
            blockedBonuses
            lockReasons {
              id
              reason
              checkData
              type
              date {
                dateTime
                formattedDateTime
              }
            }
          }
        }
      `,
    });

    return userBriefData?.data?.getUserBriefInformation ?? null;
  } catch (error) {
    console.error('Error fetching userBriefInformation at getUserBriefInformation', error);
    return null;
  }
};

const GetOwnStatsQuery = gql`
  query GetProfileProviderScreenQuery($accountId: Int!, $period: PeriodEnum!, $filter: MyBetsFilterInput, $after: String, $first: Int = 5) {
    getPublicProfile(accountId: $accountId, period: $period) {
      id
      isFavorite
      isAccountSubscribedOn
      provider {
        id
        nickname
        avatar
        avatarColor
        avatarSymbol
        profileViews
      }
      providerAccount {
        id
        bookmakerType
        bookmakerSymbols
        bookmakerAlias
        id
        balance {
          id
          amount
          currencySymbol
        }
        subscriberCount
        providerCount
        activeDays
        statResets
        portfolio {
          formattedAmount
        }
        showPortfolio
        showViews
      }
      providerAccountStatistic {
        ratingPosition
        profit {
          id
          formattedAmountWithoutCurrency
          currencySymbol
          amount
          formattedAmount
        }
        profitPercent {
          id
          formattedAmount
          amount
        }
        betsCount
        riskPercent {
          riskPercentAlias
          riskPercentTitle
        }
        oddAvg
        betAvg {
          id
          amount
          currencySymbol
          formattedAmount
        }
        turnoverAmount {
          id
          formattedAmount
        }
      }
      chartByEvents {
        values {
          eventAlias
          eventName
          betCount
          profit {
            id
            amount
            currency
            currencySymbol
            formattedAmount
          }
          percent
        }
      }
      chartProfitPercent {
        values {
          date {
            id
            dateTime
            formattedDateTime
          }
          value
        }
        label
        suffix
      }
      chartProfit {
        values {
          date {
            id
            dateTime
            formattedDateTime
          }
          value {
            id
            amount
          }
        }
        label
        suffix
      }
      chartPosition {
        values {
          date {
            id
            dateTime
            formattedDateTime
          }
          value
        }
        label
        suffix
      }
      offerSettings {
        id
        periodId
        periodFormatted
        price {
          id
          amount
          formattedAmount
        }
      }
    }
    getMyBets(accountId: $accountId, filter: $filter, after: $after, first: $first) {
      edges {
        cursor
        node {
          errorCode
          id
          isOpen
          betType
          eventName
          placedAt {
            dateTime
            formattedDateTime
          }
          direction
          sportsbookType
          closedAt {
            dateTime
            formattedDateTime
          }
          stake {
            formattedAmount
          }
          liability {
            formattedAmount
          }
          odd
          stake {
            formattedAmount
          }
          profit {
            formattedAmount
            amount
            currencySymbol
          }
          income {
            formattedAmount
            amount
            currencySymbol
          }
          receivers
          sportType {
            id
            name
            alias
            __typename
          }
          wasCopied
          provider
          errorMessage
          competitionName
          hasError
          selectionName
          isFreeBet
          hasProfitBoost
          eventName
          marketName
          selectionName
          outcome
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const getOwnStatsResponse = async (
  client: ApolloClient<NormalizedCacheObject>,
  accountId: number,
  period?: Period,
  filter?: {
    fromDate?: string;
    toDate?: string;
    betTypes?: DTOBetTypeEnum;
    outcomes?: DTOBetOutcomeEnum;
    betId?: string;
    sportTypeIds?: number[];
  },
  after?: string,
  first?: number,
  subscriberId?: number,
) => {
  const response = await client.query<Pick<DTOQuery, 'getPublicProfile' | 'getMyBets'>>({
    query: GetOwnStatsQuery,
    variables: {
      accountId,
      period,
      filter,
      after,
      first,
      subscriberId,
    },
    fetchPolicy: 'network-only',
  });

  if (response?.errors) {
    return {
      error: true,
      message: response?.errors[0]?.message,
    };
  }

  if (!response?.data) {
    return {
      error: true,
      message: 'BetsRepository > getOwnStats No data returned',
      data: response,
    };
  }

  return { data: response?.data };
};
